import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box, Typography, Button, TextField, Switch, FormControlLabel, CircularProgress,
  Alert, Table, TableHead, TableBody, TableRow, TableCell, Chip, useTheme, alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { ACCENT } from "./accent";
import { getCombination, simulate } from "./api/propulsion";
import { APP_BASE } from "./paths";

const BEST = "#2e7d32";

// Rows of the comparison table. `get` pulls the value from a sim result;
// `better` says which direction wins; `fmt` renders it.
const METRICS = [
  { key: "twr", label: "Thrust : Weight", get: (r) => r.system.thrust_to_weight, better: "high", fmt: (v) => (v == null ? "—" : `${v} : 1`) },
  { key: "hover_time", label: "Hover flight time", get: (r) => r.system.hover_flight_time_min, better: "high", fmt: (v) => (v == null ? "—" : `${v} min`) },
  { key: "hover_throttle", label: "Hover throttle", get: (r) => r.system.hover_throttle_pct, better: "low", fmt: (v) => (v == null ? "—" : `${v} %`) },
  { key: "max_thrust", label: "Max thrust", get: (r) => r.system.max_total_thrust_g, better: "high", fmt: (v) => (v == null ? "—" : `${v} g`) },
  { key: "hover_eff", label: "Motor eff. @ hover", get: (r) => r.hover?.motor_efficiency_pct, better: "high", fmt: (v) => (v == null ? "—" : `${v} %`) },
  { key: "max_eff", label: "Motor eff. @ max", get: (r) => r.max.motor_efficiency_pct, better: "high", fmt: (v) => (v == null ? "—" : `${v} %`) },
  { key: "hover_current", label: "Hover current", get: (r) => r.system.hover_current_a, better: "low", fmt: (v) => (v == null ? "—" : `${v} A`) },
  { key: "specific", label: "Specific thrust", get: (r) => r.system.specific_thrust_hover_g_per_w, better: "high", fmt: (v) => (v == null ? "—" : `${v} g/W`) },
  { key: "payload", label: "Add. payload", get: (r) => r.system.add_payload_g, better: "high", fmt: (v) => (v == null ? "—" : `${v} g`) },
];

const bestIndex = (values, better) => {
  const nums = values.map((v) => (typeof v === "number" ? v : null));
  const present = nums.filter((v) => v != null);
  if (present.length < 2) return -1; // no winner worth highlighting
  const target = better === "low" ? Math.min(...present) : Math.max(...present);
  return nums.findIndex((v) => v === target);
};

const PropulsionComparePage = () => {
  const theme = useTheme();
  const [params] = useSearchParams();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);

  const [combos, setCombos] = useState([]); // [{ id, name }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    all_up_weight_g: 850,
    num_motors: 4,
    coaxial: false,
    cells_series: 4,
    field_elevation_m: 0,
    air_temperature_c: 15,
  });
  const [results, setResults] = useState(null); // [{ id, name, result }]
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  useEffect(() => {
    if (!ids.length) {
      setError("No combinations selected.");
      setLoading(false);
      return;
    }
    Promise.all(ids.map((id) => getCombination(id)))
      .then((details) => {
        setCombos(
          details.map((d) => ({
            id: d.combination.id,
            name: d.combination.name || `${d.motor?.name} + ${d.propeller?.name}`,
          }))
        );
        const s = details[0]?.combination?.cells_series;
        if (s) setForm((f) => ({ ...f, cells_series: s }));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: key === "coaxial" ? e.target.checked : e.target.value }));

  const run = useCallback(async () => {
    setRunning(true);
    setRunError(null);
    try {
      const base = {
        all_up_weight_g: Number(form.all_up_weight_g),
        num_motors: Number(form.num_motors),
        coaxial: form.coaxial,
        cells_series: Number(form.cells_series),
        field_elevation_m: Number(form.field_elevation_m),
        air_temperature_c: Number(form.air_temperature_c),
      };
      const out = await Promise.all(
        combos.map((c) =>
          simulate({ ...base, combination_id: c.id }).then((d) => ({ ...c, result: d.result }))
        )
      );
      setResults(out);
    } catch (e) {
      setRunError(e.message);
      setResults(null);
    } finally {
      setRunning(false);
    }
  }, [combos, form]);

  const fieldSx = { flex: "1 1 130px", minWidth: 120 };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Button
        component={RouterLink}
        to={APP_BASE}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ color: theme.palette.text.secondary, textTransform: "none", mb: 2 }}
      >
        Propulsion Calculator
      </Button>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress color="secondary" size={28} />
        </Box>
      ) : error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : (
        <>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Compare {combos.length} combinations
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.5 }}>
            {combos.map((c) => (
              <Chip key={c.id} size="small" label={c.name} variant="outlined" />
            ))}
          </Box>

          {/* Shared frame inputs */}
          <Box
            sx={{
              display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mt: 3, p: 2,
              borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            }}
          >
            <TextField sx={fieldSx} size="small" type="number" label="All-up weight (g)"
              value={form.all_up_weight_g} onChange={set("all_up_weight_g")} />
            <TextField sx={fieldSx} size="small" type="number" label="# Motors"
              value={form.num_motors} onChange={set("num_motors")} />
            <TextField sx={fieldSx} size="small" type="number" label="Cells (S)"
              value={form.cells_series} onChange={set("cells_series")} />
            <TextField sx={fieldSx} size="small" type="number" label="Elevation (m)"
              value={form.field_elevation_m} onChange={set("field_elevation_m")} />
            <TextField sx={fieldSx} size="small" type="number" label="Air temp (°C)"
              value={form.air_temperature_c} onChange={set("air_temperature_c")} />
            <FormControlLabel
              control={<Switch checked={form.coaxial} onChange={set("coaxial")} size="small" />}
              label={<Typography variant="body2">Coaxial</Typography>}
            />
            <Button
              variant="contained"
              startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={run}
              disabled={running}
              sx={{ backgroundColor: ACCENT, textTransform: "none", "&:hover": { backgroundColor: "#0058A5" } }}
            >
              Run comparison
            </Button>
          </Box>

          {runError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {runError}
            </Alert>
          )}

          {results && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Theoretical estimates (±~15%) under the same airframe. Best value in each row is highlighted.
              </Alert>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 480 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
                      {results.map((r) => (
                        <TableCell key={r.id} sx={{ fontWeight: 700 }}>
                          {r.name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {METRICS.map((m) => {
                      const values = results.map((r) => m.get(r.result));
                      const winner = bestIndex(values, m.better);
                      return (
                        <TableRow key={m.key}>
                          <TableCell sx={{ color: theme.palette.text.secondary }}>{m.label}</TableCell>
                          {values.map((v, i) => (
                            <TableCell
                              key={results[i].id}
                              sx={{
                                fontWeight: i === winner ? 700 : 500,
                                color: i === winner ? BEST : theme.palette.text.primary,
                              }}
                            >
                              {m.fmt(v)}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PropulsionComparePage;
